import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Tag, 
  Coffee, 
  Package,
  AlertTriangle,
  Check,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, Category } from '../../services/dataService';

const CategoryManager: React.FC = () => {
  const { tenantData } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'menuItem' | 'rawMaterial'>('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    type: 'menuItem' as 'menuItem' | 'rawMaterial',
    description: '',
    color: '#3B82F6'
  });

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      const categoryList = await dataService.getCategories(tenantData!.id);
      setCategories(categoryList);
    } catch (error) {
      console.error('Error loading categories:', error);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, [tenantData]);

  useEffect(() => {
    if (tenantData?.id) {
      loadCategories();
    }
  }, [tenantData, loadCategories]);

  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         category.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || category.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'menuItem',
      description: '',
      color: '#3B82F6'
    });
    setEditingCategory(null);
    setShowAddForm(false);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name.trim()) {
      setError('Category name is required');
      return;
    }

    // Check for duplicate names within the same type
    const duplicateExists = categories.some(cat => 
      cat.name.toLowerCase() === formData.name.toLowerCase() && 
      cat.type === formData.type &&
      cat.id !== editingCategory?.id
    );

    if (duplicateExists) {
      setError(`A ${formData.type === 'menuItem' ? 'menu item' : 'raw material'} category with this name already exists`);
      return;
    }

    try {
      if (editingCategory) {
        await dataService.updateCategory(tenantData!.id, editingCategory.id!, {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          color: formData.color
        });
        setSuccess('Category updated successfully');
      } else {
        await dataService.createCategory(tenantData!.id, {
          name: formData.name.trim(),
          type: formData.type,
          description: formData.description.trim() || undefined,
          color: formData.color,
          isDefault: false
        });
        setSuccess('Category created successfully');
      }
      
      await loadCategories();
      resetForm();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving category:', error);
      setError('Failed to save category');
    }
  };

  const handleEdit = (category: Category) => {
    setFormData({
      name: category.name,
      type: category.type,
      description: category.description || '',
      color: category.color || '#3B82F6'
    });
    setEditingCategory(category);
    setShowAddForm(true);
  };

  const handleDelete = async (category: Category) => {
    if (!window.confirm(`Are you sure you want to delete the "${category.name}" category?`)) {
      return;
    }

    try {
      await dataService.deleteCategory(tenantData!.id, category.id!);
      setSuccess('Category deleted successfully');
      await loadCategories();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to delete category');
    }
  };

  const initializeCategories = async () => {
    try {
      setLoading(true);
      await dataService.initializeDefaultCategories(tenantData!.id);
      await loadCategories();
      setSuccess('Default categories initialized successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error initializing categories:', error);
      setError('Failed to initialize default categories');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col tablet:flex-row tablet:items-center tablet:justify-between animate-fade-in-up animation-delay-100 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50">
        <div>
          <h2 className="text-2xl tablet:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Category Management
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-base tablet:text-lg">
            Manage categories for menu items and raw materials
          </p>
        </div>
        <div className="mt-6 tablet:mt-0 flex flex-col sm:flex-row gap-3">
          {categories.length === 0 && (
            <button 
              onClick={initializeCategories}
              className="btn-secondary animate-button-hover px-6 py-3 flex items-center justify-center"
            >
              <Tag className="w-5 h-5 mr-2" />
              Initialize Default Categories
            </button>
          )}
          <button 
            onClick={() => setShowAddForm(true)}
            className="btn-primary animate-button-hover px-6 py-3 flex items-center justify-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Category
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6 animate-fade-in-up animation-delay-200">
          <div className="flex items-center space-x-3">
            <Check className="w-6 h-6 text-green-500 dark:text-green-400" />
            <p className="text-green-800 dark:text-green-300 font-medium">{success}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 animate-fade-in-up animation-delay-200">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-red-500 dark:text-red-400" />
            <p className="text-red-800 dark:text-red-300 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="card animate-fade-in-up animation-delay-300 p-8 rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mt-1">
                {editingCategory ? 'Update category details below' : 'Create a new category for your items'}
              </p>
            </div>
            <button
              onClick={resetForm}
              className="p-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 tablet:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="input-field w-full px-4 py-3 text-base"
                  placeholder="Enter category name"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Category Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as 'menuItem' | 'rawMaterial' }))}
                  className="input-field w-full px-4 py-3 text-base"
                  disabled={!!editingCategory} // Can't change type when editing
                >
                  <option value="menuItem">Menu Item</option>
                  <option value="rawMaterial">Raw Material</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="input-field w-full px-4 py-3 text-base resize-none"
                placeholder="Enter category description (optional)"
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Category Color
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="w-16 h-12 border-2 border-gray-300 dark:border-gray-600 rounded-xl cursor-pointer hover:scale-105 transition-transform shadow-sm"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Choose a color to represent this category
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={resetForm}
                className="btn-secondary animate-button-hover px-6 py-3 text-base flex items-center justify-center"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary animate-button-hover px-6 py-3 text-base flex items-center justify-center"
              >
                {editingCategory ? 'Update Category' : 'Create Category'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="card animate-fade-in-up animation-delay-400 p-6 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50">
        <div className="flex flex-col tablet:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              </div>
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field w-full pl-12 pr-4 py-3 text-base"
              />
            </div>
          </div>

          <div className="tablet:w-48">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as 'all' | 'menuItem' | 'rawMaterial')}
              className="input-field w-full px-4 py-3 text-base"
            >
              <option value="all">All Types</option>
              <option value="menuItem">Menu Items</option>
              <option value="rawMaterial">Raw Materials</option>
            </select>
          </div>
        </div>
      </div>

      {/* Categories List */}
      <div className="space-y-6">
        {filteredCategories.length === 0 ? (
          <div className="text-center py-16 animate-fade-in-up animation-delay-500 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50">
            <Tag className="w-20 h-20 text-gray-400 dark:text-gray-500 mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">No categories found</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-8 text-lg max-w-md mx-auto">
              {categories.length === 0 
                ? 'Get started by creating your first category or initializing default categories.'
                : 'Try adjusting your search or filter criteria.'
              }
            </p>
            {categories.length === 0 && (
              <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                <button 
                  onClick={initializeCategories}
                  className="btn-secondary animate-button-hover px-6 py-3 flex items-center justify-center"
                >
                  <Tag className="w-5 h-5 mr-2" />
                  Initialize Defaults
                </button>
                <button 
                  onClick={() => setShowAddForm(true)}
                  className="btn-primary animate-button-hover px-6 py-3 flex items-center justify-center"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Category
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 tablet:grid-cols-2 tablet-lg:grid-cols-3 gap-8">
            {filteredCategories.map((category, index) => (
              <div 
                key={category.id} 
                className="group relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 animate-card-hover animate-fade-in-up border border-gray-200/50 dark:border-gray-700/50 overflow-hidden"
                style={{ 
                  animationDelay: `${0.6 + index * 0.1}s`,
                  background: `linear-gradient(135deg, ${category.color || '#3B82F6'}10, ${category.color || '#3B82F6'}05)`
                }}
              >
                {/* Gradient Background Overlay */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `linear-gradient(135deg, ${category.color || '#3B82F6'}20, transparent)`
                  }}
                />
                
                {/* Header */}
                <div className="relative flex items-start justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div 
                      className="w-6 h-6 rounded-full shadow-lg ring-2 ring-white dark:ring-gray-700 group-hover:scale-110 transition-transform duration-300"
                      style={{ backgroundColor: category.color || '#3B82F6' }}
                    />
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-gray-800 dark:group-hover:text-gray-100 transition-colors mb-2">
                        {category.name}
                      </h3>
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-xl ${
                          category.type === 'menuItem' 
                            ? 'bg-orange-100 dark:bg-orange-900/30' 
                            : 'bg-blue-100 dark:bg-blue-900/30'
                        }`}>
                          {category.type === 'menuItem' ? (
                            <Coffee className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                          ) : (
                            <Package className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          )}
                        </div>
                        <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                          {category.type === 'menuItem' ? 'Menu Item' : 'Raw Material'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {category.isDefault && (
                    <span className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-bold rounded-full shadow-lg">
                      Default
                    </span>
                  )}
                </div>

                {/* Description */}
                {category.description && (
                  <div className="relative mb-8">
                    <p className="text-gray-600 dark:text-gray-300 text-base leading-relaxed">
                      {category.description}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="relative flex justify-end space-x-3 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                  <button
                    onClick={() => handleEdit(category)}
                    className="p-3 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-xl transition-all duration-200 hover:scale-105"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  {!category.isDefault && (
                    <button
                      onClick={() => handleDelete(category)}
                      className="p-3 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition-all duration-200 hover:scale-105"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>

                {/* Decorative Elements */}
                <div className="absolute top-6 right-6 opacity-10 group-hover:opacity-20 transition-opacity duration-500">
                  <div 
                    className="w-20 h-20 rounded-full"
                    style={{ backgroundColor: category.color || '#3B82F6' }}
                  />
                </div>
                
                {/* Bottom Gradient Line */}
                <div 
                  className="absolute bottom-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ backgroundColor: category.color || '#3B82F6' }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryManager; 